// Camera framing (owner 2026-11-02 presets; 2026-11-03 'give me more camera options zoomed out'). Pure numbers, no three /
// DOM: the distance presets, the pitch curve, the per-screen caps and the shadow reach that follows them. Render-only: the
// sim never reads any of this (spawn distances stay where they are; far presets get a render-side spawn-in, spawnIn.ts).

/** Camera distance presets. k multiplies the default 25 m distance. Order = the Select/View cycle and the Settings list
 *  (the owner's original five first, the far views after them; ids are saved, so never rename one). */
export const CAM_ZOOMS = [
  { id: 'default', label: 'Default', k: 1 },
  { id: 'close', label: 'Close (85%)', k: 0.85 },
  { id: 'closer', label: 'Closer (70%)', k: 0.7 },
  { id: 'closest', label: 'Closest (55%)', k: 0.55 },
  { id: 'far', label: 'Far (120%)', k: 1.2 },
  { id: 'farther', label: 'Farther (140%)', k: 1.4 },
  { id: 'farthest', label: 'Farthest (165%)', k: 1.65 },
  { id: 'overview', label: 'Overview (200%)', k: 2 },
] as const;
export type CamZoomId = typeof CAM_ZOOMS[number]['id'];
export const camZoomK = (id: string): number => (CAM_ZOOMS.find((z) => z.id === id) ?? CAM_ZOOMS[0]).k;

/** Camera pitch (degrees above the ground) for a distance multiplier k. Close presets tilt lower (more ground ahead, as
 *  before: 55 − 12·(1 − k)); far presets keep tilting up, but the slope eases off (12 → 7 °/k by Overview) so the top
 *  view stays a three-quarter view: 57.3° at Far (as before, 57.4°), 59.4° Farther, 61.7° Farthest, 64.5° Overview.
 *  Steeper keeps the ground the far view adds around the hero instead of out toward the horizon (the frame grows
 *  sideways and behind as much as ahead), keeps the far rim of the frame out of the haze and keeps the hero near the
 *  middle; easing off toward ~65° keeps foe silhouettes, heights (knock-ups, flight) and the hero's pose readable instead
 *  of flattening into a map view. */
export function camPitchDeg(k: number): number {
  if (k <= 1) return 55 - (1 - k) * 12;
  const x = Math.min(k, 2.4) - 1;
  return 55 + 12 * x - 2.5 * x * x;
}

/** Portrait screens (phones) pull back so the horizontal view isn't cramped (up to 1.3×, unchanged). */
export const portraitPull = (aspect: number): number => Math.min(1.3, Math.max(1, 1.05 / aspect));
/** total distance cap on portrait screens: Overview on a phone (2.0 × 1.3 = 2.6) would put the hero ~4 mm tall */
export const PORTRAIT_MAX_K = 2.3;
/** total distance cap on short landscape screens (a phone on its side, ≤ 560 CSS px tall): Farthest */
export const SHORT_MAX_K = 1.65;

/** Total distance multiplier (preset × portrait pull, capped per screen). minSide = the screen's short side in CSS px. */
export function camDistK(k: number, aspect: number, minSide: number): number {
  let t = k * portraitPull(aspect);
  if (aspect < 1) t = Math.min(t, Math.max(k, PORTRAIT_MAX_K)); // (never closer than the bare preset)
  else if (minSide < 560) t = Math.min(t, Math.max(1, SHORT_MAX_K));
  return t;
}

/** TRAINING free camera: its zoom multiplies on top of the preset; keep the product in the old range (0.35–2.6 of the
 *  default distance) so Overview × the wheel can't fly off to 130 m. Returns the clamped free-camera factor. */
export function clampTrainZoom(z: number, presetK: number): number {
  return Math.max(0.35 / presetK, Math.min(2.6 / presetK, z));
}

/** Sun shadow camera half-size (m) for a total distance multiplier (High quality only): ±30 m up to 1.3× (unchanged for
 *  every preset up to Far and for phones at Default), then growing with the view so the shadow edge stays out of the
 *  frame (±50 m at Overview); quantised to 5 m (a change rebuilds the shadow projection). */
export function shadowHalf(totalK: number): number {
  return Math.max(30, Math.ceil((30 * totalK) / 1.3 / 5) * 5);
}

/** sun shadow camera half-size (m), set each frame by the view from shadowHalf(); read by timeOfDay.ts and by the prop /
 *  edge-wall chunk culling (shadow casters just outside the view). High quality only. */
export const SHADOW = { half: 30 };
